import { _ as _defineProperty, g as getValueByPath, a as _toConsumableArray, c as _objectSpread2, d as _createForOfIteratorHelper, t as toCssDimension, e as debounce, f as _typeof, h as createAbsoluteElement, r as removeElement } from './helpers-d639255d.js';
import { B as BaseComponentMixin, c as config, n as normalizeComponent, b as registerComponent, u as use } from './plugins-4295ce0e.js';
import './Icon-9c764942.js';
import { F as FormElementMixin } from './FormElementMixin-f9847bcc.js';
import { _ as __vue_component__$1 } from './Input-69be569b.js';

/**
 * Extended input that provide suggestions while the user types
 * @displayName Autocomplete
 * @example ./examples/Autocomplete.md
 * @style _autocomplete.scss
 */

var script = {
  name: 'OAutocomplete',
  components: _defineProperty({}, __vue_component__$1.name, __vue_component__$1),
  mixins: [BaseComponentMixin, FormElementMixin],
  inheritAttrs: false,
  provide: function provide() {
    return {
      $elementRef: 'input'
    };
  },
  props: {
    /** @model */
    value: [Number, String],

    /** Options / suggestions */
    data: {
      type: Array,
      default: function _default() {
        return [];
      }
    },

    /**
     * Vertical size of input, optional
     * @values small, medium, large
     */
    size: String,

    /** Property of the object (if data is array of objects) to use as display text, and to keep track of selected option */
    field: {
      type: String,
      default: 'value'
    },

    /** The first option will always be pre-selected (easier to just hit enter or tab) */
    keepFirst: Boolean,

    /** Clear input text on select */
    clearOnSelect: Boolean,

    /** Open dropdown list on focus */
    openOnFocus: Boolean,

    /** Function to format an option to a string for display in the input as alternative to field prop) */
    customFormatter: Function,

    /** Makes the component check if list reached scroll end and emit infinite-scroll event. */
    checkInfiniteScroll: Boolean,

    /** Keep open dropdown list after select */
    keepOpen: Boolean,

    /** Add a button/icon to clear the inputed text */
    clearable: Boolean,

    /** Max height of dropdown content */
    maxHeight: [String, Number],

    /** 
     * Position of dropdown
     * @values auto, top, bottom
     */
    dropdownPosition: {
      type: String,
      default: 'auto'
    },

    /** Transition name to apply on dropdown list */
    animation: {
      type: String,
      default: function _default() {
        return getValueByPath(config, 'autocomplete.animation', 'fade');
      }
    },

    /** Number of milliseconds to delay before to emit typing event */
    debounceTyping: Number,

    /** Icon name to be added on the right side */
    iconRight: String,

    /** Clickable icon right if exists */
    iconRightClickable: Boolean,

    /** Append autocomplete content to body */
    appendToBody: Boolean,

    /** Root class */
    rootClass: String,

    /** Options menu class */
    menuClass: String,

    /** Expanded options menu class */
    expandedClass: String,
    openedTopClass: String,

    /** Option class */
    itemClass: String,

    /** Option hovered class */
    itemHoveredClass: String,

    /** Option disabled class */
    itemDisabledClass: String,

    /** Classes to apply on internal input (@see o-input style docs) */
    inputClasses: Object
  },
  data: function data() {
    return {
      selected: null,
      hovered: null,
      isActive: false,
      newValue: this.value,
      newAutocomplete: this.autocomplete || 'off',
      isListInViewportVertically: true,
      hasFocus: false,
      bodyEl: undefined,
      // Used to append to body
      debouncedEmitTyping: undefined // Used to debouced typing

    };
  },
  computed: {
    rootClasses: function rootClasses() {
      return [this.computedClass('autocomplete', 'rootClass', 'o-autocomplete'), _defineProperty({}, this.computedClass('autocomplete', 'expandedClass', 'o-autocomplete-expanded'), this.expanded)];
    },
    menuClasses: function menuClasses() {
      return [this.computedClass('autocomplete', 'menuClass', 'o-autocomplete-menu'), _defineProperty({}, this.computedClass('autocomplete', 'openedTopClass', 'o-autocomplete-opened-top'), this.isOpenedTop && !this.appendToBody)];
    },
    itemClasses: function itemClasses() {
      return [this.computedClass('autocomplete', 'itemClass', 'o-autocomplete-item')];
    },
    itemEmptyClasses: function itemEmptyClasses() {
      return [].concat(_toConsumableArray(this.itemClasses), [this.computedClass('autocomplete', 'itemDisabledClass', 'o-autocomplete-item-disabled')]);
    },
    inputBind: function inputBind() {
      return _objectSpread2(_objectSpread2({}, this.$attrs), this.inputClasses);
    },

    /**
     * White-listed items to not close when clicked.
     * Add input, dropdown and all children.
     */
    whiteList: function whiteList() {
      var whiteList = [];
      whiteList.push(this.$refs.input.$el.querySelector('input'));
      whiteList.push(this.$refs.dropdown); // Add all chidren from dropdown

      if (this.$refs.dropdown !== undefined) {
        var children = this.$refs.dropdown.querySelectorAll('*');

        var _iterator = _createForOfIteratorHelper(children),
            _step;

        try {
          for (_iterator.s(); !(_step = _iterator.n()).done;) {
            var child = _step.value;
            whiteList.push(child);
          }
        } catch (err) {
          _iterator.e(err);
        } finally {
          _iterator.f();
        }
      }

      if (this.$parent.$data._isTaginput) {
        // Add taginput container
        whiteList.push(this.$parent.$el); // Add .tag and .delete

        var tagInputChildren = this.$parent.$el.querySelectorAll('*');

        var _iterator2 = _createForOfIteratorHelper(tagInputChildren),
            _step2;

        try {
          for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
            var tagInputChild = _step2.value;
            whiteList.push(tagInputChild);
          }
        } catch (err) {
          _iterator2.e(err);
        } finally {
          _iterator2.f();
        }
      }

      return whiteList;
    },

    /**
     * Check if exists default slot
     */
    hasDefaultSlot: function hasDefaultSlot() {
      return !!this.$scopedSlots.default;
    },

    /**
     * Check if exists "empty" slot
     */
    hasEmptySlot: function hasEmptySlot() {
      return !!this.$slots.empty;
    },

    /**
     * Check if exists "header" slot
     */
    hasHeaderSlot: function hasHeaderSlot() {
      return !!this.$slots.header;
    },

    /**
     * Check if exists "footer" slot
     */
    hasFooterSlot: function hasFooterSlot() {
      return !!this.$slots.footer;
    },

    /**
     * Apply dropdownPosition property
     */
    isOpenedTop: function isOpenedTop() {
      return this.dropdownPosition === 'top' || this.dropdownPosition === 'auto' && !this.isListInViewportVertically;
    },
    newIconRight: function newIconRight() {
      if (this.clearable && this.newValue) {
        return 'close-circle';
      }

      return this.iconRight;
    },
    newIconRightClickable: function newIconRightClickable() {
      if (this.clearable) {
        return true;
      }

      return this.iconRightClickable;
    },
    menuStyle: function menuStyle() {
      return {
        maxHeight: toCssDimension(this.maxHeight)
      };
    }
  },
  watch: {
    /**
     * When dropdown is toggled, check the visibility to know when
     * to open upwards.
     */
    isActive: function isActive(active) {
      var _this = this;

      if (this.dropdownPosition === 'auto') {
        if (active) {
          this.calcDropdownInViewportVertical();
        } else {
          // Timeout to wait for the animation to finish before recalculating
          setTimeout(function () {
            _this.calcDropdownInViewportVertical();
          }, 100);
        }
      }

      if (active) this.$nextTick(function () {
        return _this.setHovered(null);
      });
    },

    /**
     * When updating input's value
     *   1. Emit changes
     *   2. If value isn't the same as selected, set null
     *   3. Close dropdown if value is clear or else open it
     */
    newValue: function newValue(value) {
      this.$emit('input', value); // Check if selected is invalid

      var currentValue = this.getValue(this.selected);

      if (currentValue && currentValue !== value) {
        this.setSelected(null, false);
      } // Close dropdown if input is clear or else open it


      if (this.hasFocus && (!this.openOnFocus || value)) {
        this.isActive = !!value;
      }
    },

    /**
     * When v-model is changed:
     *   1. Update internal value.
     *   2. If it's invalid, validate again.
     */
    value: function value(_value) {
      this.newValue = _value;
    },

    /**
     * Select first option if "keep-first
     */
    data: function data(value) {
      // Keep first option always pre-selected
      if (this.keepFirst) {
        this.selectFirstOption(value);
      }
    },
    debounceTyping: {
      handler: function handler(value) {
        this.debouncedEmitTyping = debounce(this, this.emitTyping, value);
      },
      immediate: true
    }
  },
  methods: {
    itemOptionClasses: function itemOptionClasses(option) {
      return [].concat(_toConsumableArray(this.itemClasses), [_defineProperty({}, this.computedClass('autocomplete', 'itemHoveredClass', 'o-autocomplete-item-hovered'), option === this.hovered)]);
    },

    /**
     * Set which option is currently hovered.
     */
    setHovered: function setHovered(option) {
      if (option === undefined) return;
      this.hovered = option;
    },

    /**
     * Set which option is currently selected, update v-model,
     * update input value and close dropdown.
     */
    setSelected: function setSelected(option) {
      var _this2 = this;

      var closeDropdown = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
      var event = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : undefined;
      if (option === undefined) return;
      this.selected = option;
      this.$emit('select', this.selected, event);

      if (this.selected !== null) {
        this.newValue = this.clearOnSelect ? '' : this.getValue(this.selected);
        this.setHovered(null);
      }

      closeDropdown && this.$nextTick(function () {
        _this2.isActive = false;
      });
      this.checkValidity();
    },

    /**
     * Select first option
     */
    selectFirstOption: function selectFirstOption(options) {
      var _this3 = this;

      this.$nextTick(function () {
        if (options.length) {
          // If has visible data or open on focus, keep updating the hovered
          if (_this3.openOnFocus || _this3.newValue !== '' && _this3.hovered !== options[0]) {
            _this3.setHovered(options[0]);
          }
        } else {
          _this3.setHovered(null);
        }
      });
    },

    /**
     * Enter key listener.
     * Select the hovered option.
     */
    enterPressed: function enterPressed(event) {
      if (this.hovered === null) return;
      this.setSelected(this.hovered, !this.keepOpen, event);
    },

    /**
     * Tab key listener.
     * Select hovered option if it exists, close dropdown, then allow
     * native handling to move to next tabbable element.
     */
    tabPressed: function tabPressed(event) {
      if (this.hovered === null) {
        this.isActive = false;
        return;
      }

      this.setSelected(this.hovered, !this.keepOpen, event);
    },

    /**
     * Close dropdown if clicked outside.
     */
    clickedOutside: function clickedOutside(event) {
      if (this.whiteList.indexOf(event.target) < 0) this.isActive = false;
    },

    /**
     * Return display text for the input.
     * If object, get value from path, or else just the value.
     */
    getValue: function getValue(option) {
      if (option === null) return;

      if (typeof this.customFormatter !== 'undefined') {
        return this.customFormatter(option);
      }

      return _typeof(option) === 'object' ? getValueByPath(option, this.field) : option;
    },

    /**
     * Check if the scroll list inside the dropdown
     * reached it's end.
     */
    checkIfReachedTheEndOfScroll: function checkIfReachedTheEndOfScroll() {
      var list = this.$refs.dropdown;

      if (list.clientHeight !== list.scrollHeight && list.scrollTop + list.clientHeight >= list.scrollHeight) {
        this.$emit('infinite-scroll');
      }
    },

    /**
     * Calculate if the dropdown is vertically visible when activated,
     * otherwise it is openened upwards.
     */
    calcDropdownInViewportVertical: function calcDropdownInViewportVertical() {
      var _this4 = this;

      this.$nextTick(function () {
        /**
        * this.$refs.dropdown may be undefined
        * when Autocomplete is conditional rendered
        */
        if (_this4.$refs.dropdown === undefined) return;

        var rect = _this4.$refs.dropdown.getBoundingClientRect();

        _this4.isListInViewportVertically = rect.top >= 0 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight);

        if (_this4.appendToBody) {
          _this4.updateAppendToBody();
        }
      });
    },

    /**
     * Arrows keys listener.
     * If dropdown is active, set hovered option, or else just open.
     */
    keyArrows: function keyArrows(direction) {
      var sum = direction === 'down' ? 1 : -1;

      if (this.isActive) {
        var index = this.data.indexOf(this.hovered) + sum;
        index = index > this.data.length - 1 ? this.data.length : index;
        index = index < 0 ? 0 : index;
        this.setHovered(this.data[index]);
        var list = this.$refs.dropdown;
        var element = list.querySelectorAll("a")[index];
        if (!element) return;
        var visMin = list.scrollTop;
        var visMax = list.scrollTop + list.clientHeight - element.clientHeight;

        if (element.offsetTop < visMin) {
          list.scrollTop = element.offsetTop;
        } else if (element.offsetTop >= visMax) {
          list.scrollTop = element.offsetTop - list.clientHeight + element.clientHeight;
        }
      } else {
        this.isActive = true;
      }
    },

    /**
     * Focus listener.
     * If value is the same as selected, select all text.
     */
    focused: function focused(event) {
      if (this.getValue(this.selected) === this.newValue) {
        this.$el.querySelector('input').select();
      }

      if (this.openOnFocus) {
        this.isActive = true;

        if (this.keepFirst) {
          this.selectFirstOption(this.data);
        }
      }

      this.hasFocus = true;
      this.$emit('focus', event);
    },

    /**
    * Blur listener.
    */
    onBlur: function onBlur(event) {
      this.hasFocus = false;
      this.$emit('blur', event);
    },
    onInput: function onInput() {
      var currentValue = this.getValue(this.selected);
      if (currentValue && currentValue === this.newValue) return;
      if (this.debounceTyping) this.debouncedEmitTyping();else this.emitTyping();
    },
    emitTyping: function emitTyping() {
      this.$emit('typing', this.newValue);
      this.checkValidity();
    },
    rightIconClick: function rightIconClick(event) {
      if (this.clearable) {
        this.newValue = '';

        if (this.openOnFocus) {
          this.$refs.input.$el.focus();
        }
      } else {
        this.$emit('icon-right-click', event);
      }
    },
    checkValidity: function checkValidity() {
      var _this5 = this;

      if (this.useHtml5Validation) {
        this.$nextTick(function () {
          _this5.checkHtml5Validity();
        });
      }
    },
    updateAppendToBody: function updateAppendToBody() {
      var dropdownMenu = this.$refs.dropdown;
      var trigger = this.$refs.input.$el;

      if (dropdownMenu && trigger) {
        // update wrapper dropdown
        var root = this.$data.bodyEl;
        root.classList.forEach(function (item) {
          return root.classList.remove(item);
        });
        this.rootClasses.forEach(function (item) {
          if (item) {
            if (_typeof(item) === 'object') {
              Object.keys(item).filter(function (key) {
                return item[key];
              }).forEach(function (key) {
                return root.classList.add(key);
              });
            } else {
              root.classList.add(item);
            }
          }
        });
        var rect = trigger.getBoundingClientRect();
        var top = rect.top + window.scrollY;
        var left = rect.left + window.scrollX;

        if (!this.isOpenedTop) {
          top += trigger.clientHeight;
        } else {
          top -= dropdownMenu.clientHeight;
        }

        dropdownMenu.style.position = 'absolute';
        dropdownMenu.style.top = "".concat(top, "px");
        dropdownMenu.style.left = "".concat(left, "px");
        dropdownMenu.style.width = "".concat(trigger.clientWidth, "px");
        dropdownMenu.style.maxWidth = "".concat(trigger.clientWidth, "px");
        dropdownMenu.style.zIndex = '9999';
      }
    }
  },
  created: function created() {
    if (typeof window !== 'undefined') {
      document.addEventListener('click', this.clickedOutside);
      if (this.dropdownPosition === 'auto') window.addEventListener('resize', this.calcDropdownInViewportVertical);
    }
  },
  mounted: function mounted() {
    var list = this.$refs.dropdown;

    if (this.checkInfiniteScroll && list) {
      list.addEventListener('scroll', this.checkIfReachedTheEndOfScroll);
    }

    if (this.appendToBody) {
      this.$data.bodyEl = createAbsoluteElement(list);
      this.updateAppendToBody();
    }
  },
  beforeDestroy: function beforeDestroy() {
    if (typeof window !== 'undefined') {
      document.removeEventListener('click', this.clickedOutside);
      if (this.dropdownPosition === 'auto') window.removeEventListener('resize', this.calcDropdownInViewportVertical);
    }

    if (this.checkInfiniteScroll && this.$refs.dropdown) {
      var list = this.$refs.dropdown;
      list.removeEventListener('scroll', this.checkIfReachedTheEndOfScroll);
    }

    if (this.appendToBody) {
      removeElement(this.$data.bodyEl);
    }
  }
};

/* script */
const __vue_script__ = script;

/* template */
var __vue_render__ = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { class: _vm.rootClasses },
    [
      _c(
        "o-input",
        _vm._b(
          {
            ref: "input",
            attrs: {
              type: "text",
              size: _vm.size,
              rounded: _vm.rounded,
              icon: _vm.icon,
              "icon-right": _vm.newIconRight,
              "icon-right-clickable": _vm.newIconRightClickable,
              "icon-pack": _vm.iconPack,
              maxlength: _vm.maxlength,
              autocomplete: _vm.newAutocomplete,
              "use-html5-validation": false
            },
            on: {
              input: _vm.onInput,
              focus: _vm.focused,
              blur: _vm.onBlur,
              "icon-right-click": _vm.rightIconClick,
              "icon-click": function(event) {
                return _vm.$emit("icon-click", event)
              }
            },
            nativeOn: {
              keyup: function($event) {
                if (
                  !$event.type.indexOf("key") &&
                  _vm._k($event.keyCode, "esc", 27, $event.key, [
                    "Esc",
                    "Escape"
                  ])
                ) {
                  return null
                }
                $event.preventDefault();
                _vm.isActive = false;
              },
              keydown: [
                function($event) {
                  if (
                    !$event.type.indexOf("key") &&
                    _vm._k($event.keyCode, "tab", 9, $event.key, "Tab")
                  ) {
                    return null
                  }
                  return _vm.tabPressed($event)
                },
                function($event) {
                  if (
                    !$event.type.indexOf("key") &&
                    _vm._k($event.keyCode, "enter", 13, $event.key, "Enter")
                  ) {
                    return null
                  }
                  $event.preventDefault();
                  return _vm.enterPressed($event)
                },
                function($event) {
                  if (
                    !$event.type.indexOf("key") &&
                    _vm._k($event.keyCode, "up", 38, $event.key, [
                      "Up",
                      "ArrowUp"
                    ])
                  ) {
                    return null
                  }
                  $event.preventDefault();
                  return _vm.keyArrows("up")
                },
                function($event) {
                  if (
                    !$event.type.indexOf("key") &&
                    _vm._k($event.keyCode, "down", 40, $event.key, [
                      "Down",
                      "ArrowDown"
                    ])
                  ) {
                    return null
                  }
                  $event.preventDefault();
                  return _vm.keyArrows("down")
                }
              ]
            },
            model: {
              value: _vm.newValue,
              callback: function($$v) {
                _vm.newValue = $$v;
              },
              expression: "newValue"
            }
          },
          "o-input",
          _vm.inputBind,
          false
        )
      ),
      _vm._v(" "),
      _c("transition", { attrs: { name: _vm.animation } }, [
        _c(
          "div",
          {
            directives: [
              {
                name: "show",
                rawName: "v-show",
                value:
                  _vm.isActive &&
                  (_vm.data.length > 0 ||
                    _vm.hasEmptySlot ||
                    _vm.hasHeaderSlot),
                expression:
                  "isActive && (data.length > 0 || hasEmptySlot || hasHeaderSlot)"
              }
            ],
            ref: "dropdown",
            class: _vm.menuClasses,
            style: _vm.menuStyle
          },
          [
            _vm.hasHeaderSlot
              ? _c("div", { class: _vm.itemClasses }, [_vm._t("header")], 2)
              : _vm._e(),
            _vm._v(" "),
            _vm._l(_vm.data, function(option, index) {
              return _c(
                "a",
                {
                  key: index,
                  class: _vm.itemOptionClasses(option),
                  on: {
                    click: function($event) {
                      return _vm.setSelected(option, undefined, $event)
                    }
                  }
                },
                [
                  _vm.hasDefaultSlot
                    ? _vm._t("default", null, { option: option, index: index })
                    : _c("span", [
                        _vm._v(
                          "\n                    " +
                            _vm._s(_vm.getValue(option, true)) +
                            "\n                "
                        )
                      ])
                ],
                2
              )
            }),
            _vm._v(" "),
            _vm.data.length === 0 && _vm.hasEmptySlot
              ? _c("div", { class: _vm.itemEmptyClasses }, [_vm._t("empty")], 2)
              : _vm._e(),
            _vm._v(" "),
            _vm.hasFooterSlot
              ? _c("div", { class: _vm.itemClasses }, [_vm._t("footer")], 2)
              : _vm._e()
          ],
          2
        )
      ])
    ],
    1
  )
};
var __vue_staticRenderFns__ = [];
__vue_render__._withStripped = true;

  /* style */
  const __vue_inject_styles__ = undefined;
  /* scoped */
  const __vue_scope_id__ = undefined;
  /* module identifier */
  const __vue_module_identifier__ = undefined;
  /* functional template */
  const __vue_is_functional_template__ = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  );

var Plugin = {
  install: function install(Vue) {
    registerComponent(Vue, __vue_component__);
  }
};
use(Plugin);

export default Plugin;
export { __vue_component__ as OAutocomplete };
